import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { FolderOpen, ExternalLink, FileText, Video, Clock3, Link2, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { SectionHeader, DLLoading, EmptyState, Slogan } from "@/components/dl/ui";

const TYPE_ICON = { document: FileText, video: Video, timeline: Clock3, url: Link2, other: FolderOpen };

export default function DLCaseRecord() {
  const { caseId, caseData } = useOutletContext();
  const [records, setRecords] = useState(null);

  useEffect(() => {
    api.get(`/dl/cases/${caseId}/record`).then(({ data }) => setRecords(data)).catch(() => setRecords([]));
  }, [caseId]);

  return (
    <div className="space-y-6">
      <SectionHeader kicker="The Case Record" title="Study the record."
        desc="This is the official information universe for the simulation. Only Commissioner-admitted material appears here." />

      <div className="dl-panel-gold rounded-md px-4 py-3 flex items-center gap-3">
        <ShieldAlert className="w-5 h-5 text-gold shrink-0" />
        <span className="dl-slogan text-xs text-gold">If it's not in the Case Record, it's not in the game.</span>
        {caseData.record_cutoff && (
          <span className="ml-auto text-[11px] font-mono text-white/50">Record cutoff: <span className="text-white/80">{String(caseData.record_cutoff).slice(0, 10)}</span></span>
        )}
      </div>

      {records === null ? <DLLoading label="Opening the case file…" /> :
        records.length === 0 ? (
          <EmptyState icon={FolderOpen} title="No admitted records yet" desc="The Commissioner hasn't admitted any source material to this Case Record." testid="dl-record-empty" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {records.map((r) => {
              const Icon = TYPE_ICON[r.source_type] || FolderOpen;
              return (
                <div key={r.record_id} className="dl-panel rounded-md p-4" data-testid="dl-record-row">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-sm bg-white/5 border border-white/10 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-gold" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-semibold text-white">{r.title}</div>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-white/40 mt-0.5">{r.source_type} · admitted {(r.admitted_at || "").slice(0, 10)}</div>
                      {r.provenance && <p className="mt-2 text-xs text-white/50">Source / citation: {r.provenance}</p>}
                      {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-gold hover:underline" data-testid="dl-record-link">Open source <ExternalLink className="w-3 h-3" /></a>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
